import EmployeeResponse, {
  AllEmployeeReportDto,
  CreateEmployeeRequest,
  DetailedEmployeeResponse,
  EmployeeStatisticsDto,
} from '../../model/Employee.model';
import axiosInstance from '../../utils/axiosInstance';

// Cache cho employee data
const employeeCache = new Map<
  number,
  { employee: DetailedEmployeeResponse; timestamp: number }
>();
const userEmployeeCache = new Map<
  number,
  { employee: EmployeeResponse; timestamp: number }
>();
const EMPLOYEE_CACHE_DURATION = 300000; // 5 phút

class EmployeeService {
  private baseURL = '/employee';

  async getEmployeeList(): Promise<EmployeeResponse[]> {
    const { data } = await axiosInstance.get(this.baseURL);
    return data;
  }

  async createEmployee(request: CreateEmployeeRequest): Promise<void> {
    await axiosInstance.post('/auth/register', request);
  }

  async getEmployeeByUserId(id: number): Promise<EmployeeResponse> {
    try {
      // Kiểm tra cache trước
      const cached = userEmployeeCache.get(id);
      const now = Date.now();

      if (cached && now - cached.timestamp < EMPLOYEE_CACHE_DURATION) {
        return cached.employee;
      }

      const { data } = await axiosInstance.get(`${this.baseURL}/user/${id}`);

      // Lưu vào cache
      userEmployeeCache.set(id, {
        employee: data,
        timestamp: now,
      });

      return data;
    } catch (error) {
      throw new Error(`Employee not found with user id: ${id}`);
    }
  }

  async getEmployeeById(id: number): Promise<DetailedEmployeeResponse> {
    try {
      // Kiểm tra cache trước
      const cached = employeeCache.get(id);
      const now = Date.now();

      if (cached && now - cached.timestamp < EMPLOYEE_CACHE_DURATION) {
        return cached.employee;
      }

      const { data } = await axiosInstance.get(`${this.baseURL}/${id}`);

      // Lưu vào cache
      employeeCache.set(id, {
        employee: data,
        timestamp: now,
      });

      return data;
    } catch (error) {
      throw new Error(`Employee not found with id: ${id}`);
    }
  }

  async getEmployeeByFundId(fundId: number): Promise<EmployeeResponse> {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseURL}/fund/${fundId}`
      );
      return data;
    } catch (error) {
      throw new Error(`Employee not found with fund id: ${fundId}`);
    }
  }

  async getEmployeeStatistics(
    employeeId: number,
    year: number,
    month: number
  ): Promise<EmployeeStatisticsDto> {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseURL}/statistics/${employeeId}/${year}/${month}`
      );
      return data;
    } catch (error) {
      throw new Error(
        `Employee statistics not found for employee id: ${employeeId}, year: ${year}, month: ${month}`
      );
    }
  }

  async getAllEmployeeStatistics(
    year: number,
    month: number
  ): Promise<AllEmployeeReportDto[]> {
    try {
      const { data } = await axiosInstance.get(
        `${this.baseURL}/statistics/all/${year}/${month}`
      );
      return data;
    } catch (error) {
      throw new Error(
        `Error retrieving employee statistics for year: ${year}, month: ${month}`
      );
    }
  }

  // Utility methods for cache management
  clearEmployeeCache(employeeId?: number): void {
    if (employeeId) {
      employeeCache.delete(employeeId);
    } else {
      employeeCache.clear();
    }
  }

  clearUserEmployeeCache(userId?: number): void {
    if (userId) {
      userEmployeeCache.delete(userId);
    } else {
      userEmployeeCache.clear();
    }
  }
}

export default new EmployeeService();
